import { useState, useEffect, useCallback } from "react";
import { fetchNavbar } from "src/api/payload/navbar";
import { isPayloadNavbarSource } from "src/utils/navbarSource";

const normalizeLocale = (locale) => (locale === "fr" ? "fr" : "en");

/**
 * Loads navbar links from Payload CMS when `navbar` is in REACT_APP_PAYLOAD_SOURCES.
 * Keeps the previous tree while a locale refetch is in flight; ignores stale responses.
 *
 * @param {string} locale - i18n language (`en` | `fr`)
 * @returns {{
 *   links: import('src/utils/mapNavbar').NavbarLink[],
 *   loading: boolean,
 *   error: string | null,
 *   refetch: () => Promise<void>,
 *   isPayloadSource: boolean,
 * }}
 */
export function useNavbar(locale) {
  const isPayloadSource = isPayloadNavbarSource();
  const normalizedLocale = normalizeLocale(locale);
  const [links, setLinks] = useState([]);
  const [loading, setLoading] = useState(isPayloadSource);
  const [error, setError] = useState(null);

  // Shared by the auto-fetch effect and refetch() — isCancelled lets the
  // effect ignore a stale response (e.g. locale changed again mid-request).
  const loadNavbar = useCallback(async (targetLocale, isCancelled = () => false) => {
    setLoading(true);
    setError(null);

    try {
      const tree = await fetchNavbar(targetLocale);
      if (!isCancelled()) setLinks(tree);
    } catch (err) {
      if (isCancelled()) return;
      console.error("Error fetching navbar:", err);
      setError(err?.message || "Failed to load navbar");
      setLinks([]);
    } finally {
      if (!isCancelled()) setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (!isPayloadSource) return;

    let cancelled = false;
    loadNavbar(normalizedLocale, () => cancelled);

    return () => {
      cancelled = true;
    };
  }, [isPayloadSource, normalizedLocale, loadNavbar]);

  const refetch = useCallback(() => {
    if (!isPayloadSource) return Promise.resolve();
    return loadNavbar(normalizedLocale);
  }, [isPayloadSource, normalizedLocale, loadNavbar]);

  return {
    links,
    loading,
    error,
    refetch,
    isPayloadSource,
  };
}
